import React, { useRef } from "react";
import { Camera, User } from "lucide-react";
import { toast } from "sonner";

/**
 * Resize an image file to a square JPEG data URL.
 * Returns null if the file isn't an image.
 */
export async function fileToSquareDataUrl(file, size = 256, quality = 0.85) {
    if (!file || !file.type.startsWith("image/")) return null;
    const bitmap = await createImageBitmap(file);
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");
    // Cover-fit crop to square
    const srcSize = Math.min(bitmap.width, bitmap.height);
    const sx = (bitmap.width - srcSize) / 2;
    const sy = (bitmap.height - srcSize) / 2;
    ctx.drawImage(bitmap, sx, sy, srcSize, srcSize, 0, 0, size, size);
    return canvas.toDataURL("image/jpeg", quality);
}

export function initialsOf(user) {
    if (!user) return "?";
    const n = (user.full_name || "").trim();
    if (n) {
        const parts = n.split(/\s+/);
        return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase();
    }
    return (user.email || "?").charAt(0).toUpperCase();
}

/**
 * Read-only avatar: renders user.picture if set, otherwise initials on blue.
 * Size via `className` (width/height). Set `icon` to use the User icon instead of initials.
 */
export function Avatar({ user, className = "w-10 h-10", icon = false, testId }) {
    const pic = user?.picture;
    const text = initialsOf(user);
    return (
        <div
            data-testid={testId}
            className={`rounded-full bg-[#007AFF] text-white flex items-center justify-center overflow-hidden font-bold ${className}`}
        >
            {pic ? (
                <img
                    src={pic}
                    alt={user?.full_name || user?.email || "Profile"}
                    className="w-full h-full object-cover"
                />
            ) : icon ? (
                <User className="w-[55%] h-[55%]" />
            ) : (
                <span className="text-[0.9em]">{text}</span>
            )}
        </div>
    );
}

/**
 * Clickable avatar that opens a file picker, resizes, and calls onPick(dataUrl).
 * Shows a little camera badge. Sizing via `sizeClass`.
 */
export function AvatarUpload({ user, value, onPick, sizeClass = "w-20 h-20", testId = "avatar-upload" }) {
    const inputRef = useRef(null);

    const handleFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        if (file.size > 10 * 1024 * 1024) {
            toast.error("Image too large (max 10 MB)");
            return;
        }
        try {
            const dataUrl = await fileToSquareDataUrl(file, 256, 0.85);
            if (!dataUrl) return toast.error("Not an image file");
            onPick(dataUrl);
        } catch (err) {
            toast.error("Couldn't read image");
        }
    };

    const current = value ?? user?.picture;
    const fakeUser = current ? { picture: current, full_name: user?.full_name, email: user?.email } : user;

    return (
        <div className="relative inline-block">
            <button
                type="button"
                onClick={() => inputRef.current?.click()}
                data-testid={testId}
                className={`group block rounded-full border-2 border-[#2A3655] hover:border-[#00A3FF] transition overflow-hidden ${sizeClass}`}
                aria-label="Upload profile picture"
            >
                <Avatar user={fakeUser} className="w-full h-full" icon={!current} />
            </button>
            <span className="pointer-events-none absolute -bottom-0.5 -right-0.5 w-7 h-7 rounded-full bg-[#0A1128] border border-[#2A3655] flex items-center justify-center">
                <Camera className="w-3.5 h-3.5 text-[#00A3FF]" />
            </span>
            <input
                ref={inputRef}
                type="file"
                accept="image/*"
                onChange={handleFile}
                className="hidden"
                data-testid={`${testId}-input`}
            />
        </div>
    );
}
